package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnit
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraUnitSuggestion
import com.composables.icons.lucide.Building
import com.composables.icons.lucide.Lucide
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_cancel
import portalvv.shared.generated.resources.compras_extra_units_all_set
import portalvv.shared.generated.resources.compras_extra_units_confirm
import portalvv.shared.generated.resources.compras_extra_units_message
import portalvv.shared.generated.resources.compras_extra_units_none
import portalvv.shared.generated.resources.compras_extra_units_pending
import portalvv.shared.generated.resources.compras_extra_units_placeholder
import portalvv.shared.generated.resources.compras_extra_units_search
import portalvv.shared.generated.resources.compras_extra_units_title
import portalvv.shared.generated.resources.compras_orders

@Composable
fun PurchasesExtraUnitReviewDialog(
    suggestions: List<PurchasesExtraUnitSuggestion>,
    units: List<PurchasesExtraUnit>,
    orderCounts: Map<String, Int>,
    mappings: Map<String, String?>,
    saving: Boolean,
    onSelect: (label: String, unitId: String?) -> Unit,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    val noneLabel = stringResource(Res.string.compras_extra_units_none)
    val placeholder = stringResource(Res.string.compras_extra_units_placeholder)
    val labels = remember(units) { units.labels() }
    val options = remember(labels, noneLabel) { listOf(noneLabel) + labels.values }
    val pending = suggestions.count { mappings[it.label] == null }

    AppModalCard(title = stringResource(Res.string.compras_extra_units_title), onDismiss = onDismiss) {
        Text(
            text = stringResource(Res.string.compras_extra_units_message),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        if (units.isEmpty()) {
            Text(
                text = stringResource(Res.string.compras_extra_units_none),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Text(
            text =
                if (pending > 0) {
                    stringResource(Res.string.compras_extra_units_pending, pending, suggestions.size)
                } else {
                    stringResource(Res.string.compras_extra_units_all_set)
                },
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        LazyColumn(modifier = Modifier.fillMaxWidth().heightIn(max = 420.dp)) {
            items(suggestions, key = { it.label }) { suggestion ->
                val selectedUnitId = mappings[suggestion.label]
                val selectedLabel = selectedUnitId?.let(labels::get).orEmpty()
                Column(
                    modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8),
                    verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = suggestion.label,
                            style = MaterialTheme.typography.titleSmall,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f),
                        )
                        Text(
                            text = stringResource(Res.string.compras_orders, orderCounts[suggestion.label] ?: 0),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    SearchableSelect(
                        leadingIcon = Lucide.Building,
                        options = options,
                        selected = selectedLabel,
                        onSelect = { label ->
                            onSelect(
                                suggestion.label,
                                if (label == noneLabel) {
                                    null
                                } else {
                                    labels.entries.firstOrNull { it.value == label }?.key
                                },
                            )
                        },
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = placeholder,
                        searchPlaceholder = stringResource(Res.string.compras_extra_units_search),
                        searchable = true,
                        underlined = true,
                        enabled = !saving,
                    )
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.compras_action_cancel),
                onClick = onDismiss,
                variant = PortalButtonVariant.Outlined,
                enabled = !saving,
            )
            AppButton(
                text = stringResource(Res.string.compras_extra_units_confirm),
                onClick = onConfirm,
                enabled = !saving,
            )
        }
    }
}

private fun List<PurchasesExtraUnit>.labels(): Map<String, String> {
    val repeated = groupingBy { it.name }.eachCount().filterValues { it > 1 }.keys
    return associate { unit ->
        unit.id to if (unit.name in repeated && unit.director != null) "${unit.name} — ${unit.director}" else unit.name
    }
}
